import Vue from "vue";
import Vuex from "vuex";
import VuexPersistence from "vuex-persist";
import { Federation } from "@/models/Federation";
import { Calculation } from "@/models/Calculation";
import apiClient from "@/services/api.client";
import { AxiosError } from "axios";
import { YearRange } from "@/types";
import ContingentPayload from "@/types/payloads/contingent-payload";
import ContingentErrorResponse from "@/types/responses/contingent-response";

Vue.use(Vuex);

export enum Getters {
  ASSOCIATIONS = "associations",
  CALCULATIONS = "calculations",
  OPEN_CALCULATION = "openCalculation",
  TOAST_MESSAGES = "toastMessages",
  OPEN_CONTINGENT = "getOpenContingent",
}

export enum Mutations {
  SET_OPEN_CALCULATION = "setOpenCalculation",
  SET_CALCULATIONS = "setCalculations",
  SET_FEDERATIONS = "setFederations",
  SET_OPEN_CONTINGENT = "setOpenContingent",
  ADD_TOAST_MESSAGE = "addToastMessage",
}

export enum Actions {
  FETCH_FEDERATIONS = "fetchFederations",
  FETCH_CALCULATIONS = "fetchCalculations",
  COPY_CALCULATIONS = "copyCalculations",
  GET_CONTINGENT = "getContingent",
}

export interface State {
  associations: Array<Federation> | undefined;
  calculations: Array<Calculation> | undefined;
  openCalculation: Calculation | undefined;
  openContingent: ContingentErrorResponse | undefined;
  toastMessages: Array<string>;
}

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
  reducer: (state: State) => ({
    openCalculation: state.openCalculation,
  }), // This reducer will make sure that just user data is stored between sessions.
});

export default new Vuex.Store({
  state: {
    associations: undefined,
    calculations: undefined,
    openCalculation: undefined,
    toastMessages: [],
    openContingent: undefined,
  } as State,
  getters: {
    associations: (state) => {
      return state.associations;
    },
    calculations: (state) => {
      return state.calculations;
    },
    openCalculation: (state) => {
      return state.openCalculation;
    },
    getOpenContingent: (state) => {
      return state.openContingent;
    },
    toastMessages: (state) => state.toastMessages,
  },
  // Mutations cannot be async
  mutations: {
    setFederations(state, payload) {
      state.associations = payload;
    },
    setCalculations(state, payload) {
      state.calculations = payload;
    },
    setOpenCalculation(state, payload) {
      state.openCalculation = payload;
    },
    addToastMessage(state, message) {
      state.toastMessages = [...state.toastMessages, message];
    },
    setOpenContingent(state, payload) {
      state.openContingent = payload;
    },
  },
  // Actions can be async
  actions: {
    getContingent({ commit }, input: ContingentPayload) {
      return new Promise((resolve) => {
        apiClient
          .getContingent(input.year, input.nhoNumber)
          .then((contingent) => {
            commit(Mutations.SET_OPEN_CONTINGENT, contingent);
            resolve("success");
          })
          .catch((error) => {
            if (error.message.includes("404")) {
              commit(
                Mutations.ADD_TOAST_MESSAGE,
                `Fant ingen kontingent for nhonr ${input.nhoNumber} i ${input.year}`
              );
            } else {
              commit(
                Mutations.ADD_TOAST_MESSAGE,
                "Klarte ikke å hente kontingent"
              );
            }
          });
      });
    },
    fetchFederations({ commit }) {
      apiClient
        .GetFederations()
        .then((federations) => commit(Mutations.SET_FEDERATIONS, federations))
        .catch(() =>
          commit(Mutations.ADD_TOAST_MESSAGE, "Klarte ikke å hente foreninger")
        );
    },
    fetchCalculations({ commit }) {
      return new Promise((resolve) => {
        apiClient
          .GetCalculations()
          .then((calculations) => {
            commit(Mutations.SET_CALCULATIONS, calculations);
            resolve("success");
          })
          .catch(() =>
            commit(
              Mutations.ADD_TOAST_MESSAGE,
              "Klarte ikke å hente kalkulasjoner"
            )
          );
      });
    },
    copyCalculations({ dispatch }, yearRange: YearRange) {
      return new Promise((resolve, reject) => {
        apiClient
          .copyCalculations(yearRange)
          .then(({ copiedCalculations, copiedInvoiceData }) => {
            dispatch(Actions.FETCH_CALCULATIONS);
            if (copiedCalculations > 0 || copiedInvoiceData > 0) {
              const calcText =
                copiedCalculations === 1 ? "kalkulasjon" : "kalkulasjoner";
              resolve(
                `Kopierte ${copiedCalculations} ${calcText} og ${copiedInvoiceData} faktureringsdata fra ${yearRange.from} til ${yearRange.to}.`
              );
            } else {
              resolve(
                "Ingen kalkulasjoner eller faktureringsdata ble kopiert da de allerede eksisterer."
              );
            }
          })
          .catch((error: AxiosError) => {
            console.log(error);
            const errorMessage =
              error.response?.data[0].errorMessage ??
              "Klarte ikke å kopiere data.";
            reject(errorMessage);
          });
      });
    },
  },
  plugins: [vuexLocal.plugin],
});
